/**
 * Consent-gated capture of ad click identifiers for server-side conversions
 * (#2499, docs/engineering/ad-conversions.md).
 *
 * On landing, reads `fbclid`, `ttclid`, `twclid` and `gclid` from the URL and
 * the Meta Pixel's `_fbp` / `_fbc` cookies, and hands them to the backend with
 * POST /analytics/attribution (and the signup verify-otp call), which passes
 * them to Meta (user_data.fbc / fbp), TikTok (user.ttclid) and X
 * (identifiers twclid). GA4's Measurement Protocol takes no gclid; the server
 * only stores it.
 *
 * Same consent rules as the pixels (analyticsConsent.js):
 * - Nothing is written to storage, and nothing is sent, before the analytics
 *   consent grant. URL ids seen before a decision are held in memory only and
 *   written on Accept.
 * - Token-link pages (email preferences) never capture.
 * - Reject, or a reset of the choice, clears the stored ids and the held ones.
 *
 * Storage: one localStorage envelope, first-touch per key, expiring 7 days
 * after the first capture. Values are passed on unmodified (Meta: click ids
 * are case sensitive). Meta's fbc is `fb.1.<ms>.<fbclid>`, with <ms> the time
 * the fbclid was first seen
 * (https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/fbp-and-fbc).
 */

import { getAnalyticsConsent, subscribeAnalyticsConsent } from './analyticsConsent';
import { isAnalyticsExcludedPath } from '../utils/sensitiveLinkToken';

export const AD_CLICK_IDS_STORAGE_KEY = 'budgit_ad_click_ids';
export const AD_CLICK_IDS_TTL_MS = 7 * 24 * 60 * 60 * 1000;
export const AD_CLICK_ID_KEYS = Object.freeze(['fbc', 'fbp', 'ttclid', 'twclid', 'gclid']);

const URL_PARAM_KEYS = ['ttclid', 'twclid', 'gclid'];
// Matches the server limit (backend/services/adConversions/clickIds.js).
const TOKEN_PATTERN = /^[A-Za-z0-9._~-]{1,500}$/;
// `fb.1.<13-digit ms>.` adds 19 characters, so the built fbc stays within 500.
const MAX_FBCLID_LENGTH = 480;

/** Click ids seen before a consent decision; memory only. */
let pendingClickIds = null;
let consentListenerInstalled = false;

function safeStorage() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    return window.localStorage;
  } catch (_) {
    return null;
  }
}

function pathname() {
  if (typeof window === 'undefined' || !window.location) return '';
  return window.location.pathname || '';
}

function cleanToken(value) {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return TOKEN_PATTERN.test(trimmed) ? trimmed : null;
}

function readCookie(name) {
  try {
    if (typeof document === 'undefined' || !document.cookie) return null;
    const prefix = `${name}=`;
    const match = document.cookie.split(';').map((part) => part.trim()).find((part) => part.startsWith(prefix));
    return match ? cleanToken(decodeURIComponent(match.slice(prefix.length))) : null;
  } catch (_) {
    return null;
  }
}

/** Meta Pixel cookies. They only exist after fbevents.js ran, i.e. after the grant. */
function readMetaCookies() {
  const ids = {};
  const fbc = readCookie('_fbc');
  const fbp = readCookie('_fbp');
  if (fbc) ids.fbc = fbc;
  if (fbp) ids.fbp = fbp;
  return ids;
}

function readUrlClickIds(now) {
  if (typeof window === 'undefined' || !window.location) return {};
  let params;
  try {
    params = new URLSearchParams(window.location.search || '');
  } catch (_) {
    return {};
  }
  const ids = {};
  const fbclid = cleanToken(params.get('fbclid'));
  // An oversize fbclid is dropped, never truncated: a cut id matches nothing.
  if (fbclid && fbclid.length <= MAX_FBCLID_LENGTH) ids.fbc = `fb.1.${now}.${fbclid}`;
  URL_PARAM_KEYS.forEach((key) => {
    const value = cleanToken(params.get(key));
    if (value) ids[key] = value;
  });
  return ids;
}

function hasAny(ids) {
  return Boolean(ids) && AD_CLICK_ID_KEYS.some((key) => ids[key]);
}

function readEnvelope() {
  const storage = safeStorage();
  if (!storage) return null;
  try {
    const raw = storage.getItem(AD_CLICK_IDS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || !hasAny(parsed.clickIds)) return null;
    if (typeof parsed.expiresAt !== 'number' || parsed.expiresAt < Date.now()) {
      storage.removeItem(AD_CLICK_IDS_STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch (_) {
    return null;
  }
}

/** First-touch per key: stored keys win, new keys are added. */
function persist(ids, now) {
  const storage = safeStorage();
  if (!storage || !hasAny(ids)) return false;
  const existing = readEnvelope();
  const merged = { ...ids, ...(existing ? existing.clickIds : {}) };
  const envelope = existing
    ? { ...existing, clickIds: merged }
    : { clickIds: merged, capturedAt: now, expiresAt: now + AD_CLICK_IDS_TTL_MS };
  try {
    storage.setItem(AD_CLICK_IDS_STORAGE_KEY, JSON.stringify(envelope));
    return true;
  } catch (_) {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Pending server revoke (analyticsService.js sends it). Lives here so api.js
// can clear it without importing analyticsService (which imports api).
// ---------------------------------------------------------------------------

export const AD_CLICK_IDS_REVOKE_PENDING_KEY = 'budgit_ad_click_ids_revoke_pending';

export function setRevokePending(pending) {
  const storage = safeStorage();
  if (!storage) return;
  try {
    if (pending) storage.setItem(AD_CLICK_IDS_REVOKE_PENDING_KEY, '1');
    else storage.removeItem(AD_CLICK_IDS_REVOKE_PENDING_KEY);
  } catch (_) {}
}

export function isRevokePending() {
  const storage = safeStorage();
  try {
    return Boolean(storage && storage.getItem(AD_CLICK_IDS_REVOKE_PENDING_KEY));
  } catch (_) {
    return false;
  }
}

/**
 * verify-otp created a new account: it cannot hold ids from before an earlier
 * withdrawal, so a pending revoke would only delete the consented ids this
 * signup just stored (reject, then accept, then sign up).
 */
export function onNewAccountCreated() {
  setRevokePending(false);
}

export function clearAdClickIds() {
  pendingClickIds = null;
  const storage = safeStorage();
  if (!storage) return;
  try {
    storage.removeItem(AD_CLICK_IDS_STORAGE_KEY);
  } catch (_) {
    // Private mode: nothing was stored.
  }
}

/**
 * Call on every route mount/change. Writes only after the analytics consent
 * grant; before a decision the URL ids are held in memory for Accept.
 * @returns {boolean} true when something was stored
 */
export function captureAdClickIdsFromCurrentUrl() {
  if (typeof window === 'undefined') return false;
  if (isAnalyticsExcludedPath(pathname())) return false;
  const now = Date.now();
  const consent = getAnalyticsConsent();
  if (consent === 'denied') return false;
  const urlIds = readUrlClickIds(now);
  if (consent !== 'granted') {
    if (hasAny(urlIds)) pendingClickIds = { ...urlIds, ...(pendingClickIds || {}) };
    return false;
  }
  return persist({ ...readMetaCookies(), ...urlIds }, now);
}

/**
 * Click ids to send now, or null. Null without the grant or on a token-link
 * page. Meta cookies are re-read here because fbevents.js sets _fbp after
 * the grant, possibly after the landing capture.
 * @returns {Object|null} { fbc?, fbp?, ttclid?, twclid?, gclid? }
 */
export function getConsentedAdClickIds() {
  if (typeof window === 'undefined') return null;
  if (getAnalyticsConsent() !== 'granted') return null;
  if (isAnalyticsExcludedPath(pathname())) return null;
  const envelope = readEnvelope();
  const ids = { ...readMetaCookies(), ...(envelope ? envelope.clickIds : {}) };
  const result = {};
  AD_CLICK_ID_KEYS.forEach((key) => {
    if (ids[key]) result[key] = ids[key];
  });
  return hasAny(result) ? result : null;
}

function installConsentListener() {
  if (consentListenerInstalled || typeof window === 'undefined') return;
  consentListenerInstalled = true;
  subscribeAnalyticsConsent((choice) => {
    if (choice === 'granted') {
      const held = pendingClickIds;
      pendingClickIds = null;
      if (held && !isAnalyticsExcludedPath(pathname())) persist(held, Date.now());
      captureAdClickIdsFromCurrentUrl();
    } else {
      // Reject, or the footer reset: nothing stays behind.
      clearAdClickIds();
    }
  });
}

installConsentListener();

export function resetAdClickIdsForTests() {
  pendingClickIds = null;
}
